import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StoryTree from './StoryTree';
import NodeEditModal from './NodeEditModal';
import FullscreenStoryTree from './FullscreenStoryTree';
import { StoryNode, StoryEdge, StoryBranch } from '../services/api';

interface StoryTreePanelProps {
  nodes: StoryNode[];
  edges: StoryEdge[];
  branches: StoryBranch[];
  onNodeClick?: (nodeId: string, path?: string[]) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
  onNodeSave?: (nodeId: string, updatedData: Partial<StoryNode>) => void;
  onNodeDelete?: (nodeId: string) => void;
  onGenerateBranches?: (nodeId: string, path?: string[], allNodes?: StoryNode[], allEdges?: StoryEdge[], storyType?: string, storyStyle?: string, branchCount?: number) => void;
  isLoading?: boolean;
  branchCount: number;
  storyType: string;
  storyStyle: string;
}

const StoryTreePanel: React.FC<StoryTreePanelProps> = ({
  nodes,
  edges,
  branches,
  onNodeClick,
  onNodeDoubleClick,
  onNodeSave,
  onNodeDelete,
  onGenerateBranches,
  isLoading = false,
  branchCount,
  storyType,
  storyStyle
}) => {
  const { t } = useTranslation();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<StoryNode | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<StoryBranch | null>(null);
  const [storyPath, setStoryPath] = useState<string[]>([]);
  const [isFullscreenVisible, setIsFullscreenVisible] = useState(false);
  const [localNodes, setLocalNodes] = useState(nodes);
  const [localEdges, setLocalEdges] = useState(edges);

  React.useEffect(() => { setLocalNodes(nodes); }, [nodes]);
  React.useEffect(() => { setLocalEdges(edges); }, [edges]);

  const handleAddChildNode = (parentId: string, nodeData: { label: string; title: string; description: string; content: string }) => {
    const newId = `node_${Date.now()}_${Math.floor(Math.random()*10000)}`;
    setLocalNodes(prev => [...prev, { id: newId, ...nodeData }]);
    setLocalEdges(prev => [...prev, { from: parentId, to: newId }]);
  };

  const buildPathToNode = (targetNodeId: string): string[] => {
    const path: string[] = [];
    const visited = new Set<string>();
    
    const nodeIds = new Set(localNodes.map(n => n.id));
    const hasIncomingEdge = new Set(localEdges.map(e => e.to));
    const rootNodes = Array.from(nodeIds).filter(id => !hasIncomingEdge.has(id));
    
    const dfs = (currentNodeId: string, currentPath: string[]): boolean => {
      if (visited.has(currentNodeId)) return false;
      visited.add(currentNodeId);
      
      const newPath = [...currentPath, currentNodeId];
      
      if (currentNodeId === targetNodeId) {
        path.push(...newPath);
        return true;
      }
      
      const outgoingEdges = localEdges.filter(e => e.from === currentNodeId);
      for (const edge of outgoingEdges) {
        if (dfs(edge.to, newPath)) {
          return true;
        }
      }
      
      return false;
    };
    
    for (const rootId of rootNodes) {
      visited.clear();
      if (dfs(rootId, [])) {
        break;
      }
    }
    
    return path;
  };

  const handleNodeClick = (nodeId: string) => {
    setSelectedNodeId(nodeId);
    
    const pathToNode = buildPathToNode(nodeId);
    setStoryPath(pathToNode);
    
    // Find related branch
    const relatedBranch = branches.find(branch => branch.id === nodeId);
    setSelectedBranch(relatedBranch || null);
    
  };

  const handleNodeDoubleClick = (nodeId: string) => {
    const node = localNodes.find(n => n.id === nodeId);
    if (node) {
      setEditingNode(node);
      setIsEditModalVisible(true);
    }
    onNodeDoubleClick?.(nodeId);
  };

  const handleNodeEdit = (nodeId: string) => {
    const node = localNodes.find(n => n.id === nodeId);
    if (node) {
      setEditingNode(node);
      setIsEditModalVisible(true);
    }
  };

  const handleNodeSave = (nodeId: string | null, updatedData: Partial<StoryNode>) => {
    if (onNodeSave) {
      Promise.resolve(onNodeSave(nodeId!, updatedData)).finally(() => {
        setIsEditModalVisible(false);
        setEditingNode(null);
      });
    } else {
      setIsEditModalVisible(false);
      setEditingNode(null);
    }
  };

  const handleModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingNode(null);
  };

  const handleBranchClick = (branch: StoryBranch) => {
    setSelectedBranch(branch);
    setSelectedNodeId(branch.id);
    
    // Find related node and trigger selection
    const relatedNode = localNodes.find(node => node.id === branch.id);
    if (relatedNode) {
      onNodeClick?.(branch.id);
    }
  };

  // 在组件内添加isRoot工具函数
  const isRoot = (id: string) => {
    const nodeIds = new Set(localNodes.map(n => n.id));
    const hasIncomingEdge = new Set(localEdges.map(e => e.to));
    const rootNodes = Array.from(nodeIds).filter(id => !hasIncomingEdge.has(id));
    return rootNodes.includes(id);
  };

  if (nodes.length === 0) {
    return null;
  }

  return (
    <div className="story-tree-panel">
      <div className="card">
        <div className="card-title">
          <span role="img" aria-label="tree">🌳</span> {t('storyTree.title')}
        </div>
        
        <div className="tree-container">
          <div 
            style={{
              height: '400px',
              width: '100%'
            }}
          >
            <StoryTree 
              nodes={localNodes} 
              edges={localEdges} 
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onNodeEdit={handleNodeEdit}
              onNodeDelete={onNodeDelete}
              selectedNodeId={selectedNodeId}
              storyPath={storyPath}
              editingNodeId={editingNode?.id}
              // isLoading={isLoading}
              onAddChildNode={handleAddChildNode} // 新增
            />
          </div>
          
          {(branches.length > 0 || selectedBranch) && (
            <div className="branch-panel" style={{
              marginTop: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(76, 201, 240, 0.2)'
            }}>
              <div className="branch-header" style={{
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(76, 201, 240, 0.2)'
              }}>
                <h4 style={{
                  margin: 0,
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: 0.9
                }}>
                  <span role="img" aria-label="branch">🌱</span> {t('storyTreePanel.plotDetails')}
                  {selectedBranch && !isRoot(selectedBranch.id) && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '14px',
                      opacity: 0.7
                    }}>
                      - {selectedBranch.title}
                    </span>
                  )}
                </h4>
              </div>
              
              {selectedBranch ? (
                <div className="selected-branch-detail">
                  {storyPath.length > 1 && (
                    <div className="story-path-display" style={{
                      background: 'rgba(67, 97, 238, 0.1)',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      border: '1px solid rgba(67, 97, 238, 0.3)'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#4cc9f0',
                        marginBottom: '8px'
                      }}>
                        📍 {t('storyTreePanel.storyPath')}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        alignItems: 'center'
                      }}>
                        {storyPath.map((nodeId, index) => {
                          const node = localNodes.find(n => n.id === nodeId);
                          return (
                            <React.Fragment key={nodeId}>
                              <span style={{
                                background: index === storyPath.length - 1 
                                  ? 'rgba(76, 201, 240, 0.3)' 
                                  : 'rgba(255, 255, 255, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                border: index === storyPath.length - 1 
                                  ? '1px solid rgba(76, 201, 240, 0.5)' 
                                  : '1px solid rgba(255, 255, 255, 0.2)'
                              }}>
                                {node?.label || nodeId.slice(0, 8)}
                              </span>
                              {index < storyPath.length - 1 && (
                                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>→</span>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="branch-content" style={{
                    background: 'rgba(67, 97, 238, 0.1)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(67, 97, 238, 0.3)',
                    marginBottom: '16px'
                  }}>
                    {!isRoot(selectedBranch.id) && (
                      <div className="branch-title" style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#ffffff',
                        marginBottom: '12px'
                      }}>
                        {selectedBranch.title}
                      </div>
                    )}
                    <div className="branch-desc" style={{
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      lineHeight: '1.6',
                      marginBottom: '12px'
                    }}>
                      {selectedBranch.description}
                    </div>
                    {selectedBranch.created_at && (
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.5)',
                        marginBottom: '16px'
                      }}>
                        {t('storyTreePanel.createdAt')}: {new Date(selectedBranch.created_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  {onGenerateBranches && selectedNodeId && (
                    <button
                      onClick={() => onGenerateBranches(selectedNodeId, storyPath, localNodes, localEdges, storyType, storyStyle, branchCount)}
                      disabled={isLoading}
                      className="btn"
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                        opacity: isLoading ? 0.7 : 1,
                      }}
                    >
                      {isLoading ? t('storyInput.buttons.generating') : `🌱 ${t('storyTreePanel.generateBranches', { count: branchCount })}`}
                    </button>
                  )}
                </div>
              ) : (
                <div className="branches-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {branches.map((branch) => (
                    <div 
                      key={branch.id} 
                      className="branch-card"
                      style={{
                        background: selectedNodeId === branch.id 
                          ? 'rgba(67, 97, 238, 0.2)' 
                          : 'rgba(255, 255, 255, 0.08)',
                        border: selectedNodeId === branch.id 
                          ? '2px solid rgba(67, 97, 238, 0.5)' 
                          : '1px solid rgba(76, 201, 240, 0.2)',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                      onClick={() => handleBranchClick(branch)}
                      onMouseEnter={(e) => {
                        if (selectedNodeId !== branch.id) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                          e.currentTarget.style.borderColor = 'rgba(76, 201, 240, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedNodeId !== branch.id) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(76, 201, 240, 0.2)';
                        }
                      }}
                    >
                      <div className="branch-title" style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#ffffff',
                        marginBottom: '6px'
                      }}>
                        {branch.title}
                      </div>
                      <div className="branch-desc" style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {branch.description}
                      </div>
                      {selectedNodeId === branch.id && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '8px',
                          height: '8px',
                          background: '#4cc9f0',
                          borderRadius: '50%',
                          boxShadow: '0 0 8px rgba(76, 201, 240, 0.6)'
                        }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {branches.length > 0 && !selectedBranch && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  textAlign: 'center'
                }}>
                  {t('storyTreePanel.clickToViewDetails')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <NodeEditModal
        node={editingNode}
        isVisible={isEditModalVisible}
        onSave={handleNodeSave}
        onCancel={handleModalCancel}
        isLoading={isLoading}
      />
      
      <FullscreenStoryTree
        nodes={localNodes}
        edges={localEdges}
        branches={branches}
        isVisible={isFullscreenVisible}
        onClose={() => setIsFullscreenVisible(false)}
        onNodeSave={onNodeSave}
        onNodeDelete={onNodeDelete}
        onGenerateBranches={onGenerateBranches}
        isLoading={isLoading}
        initialSelectedNodeId={selectedNodeId || undefined}
        initialStoryPath={storyPath}
        branchCount={branchCount}
        storyType={storyType}
        storyStyle={storyStyle}
      />
    </div>
  );
};

export default StoryTreePanel;